// frontend/src/hooks/useSiteIsHebrew.js
//
// The site language (utils/siteLanguage.js) for a component that can be
// HYDRATED from a prerendered snapshot - backlog #464.
//
// THE PROBLEM. Every prerendered page is snapshotted in Hebrew (the prerender
// browser has empty localStorage). A visitor who chose English used to get
// `useState(readSiteIsHebrew)` on the hydration pass, i.e. English text over
// Hebrew HTML - a text mismatch, which React 19 answers by throwing the page's
// boundary away and rendering it again from scratch.
//
// THE RULE, per render:
//   - a choice made with this page's own toggle always wins;
//   - otherwise, on the hydration pass of a prerendered snapshot: Hebrew,
//     exactly what the snapshot holds;
//   - otherwise (every later render, and EVERY render of a client-only boot -
//     a route change, the dev server, an authenticated boot): the stored
//     preference. useHydrated() is true from the first render there, so an
//     English visitor arriving by client navigation never sees a Hebrew frame.
//
// The hydrating English visitor sees the Hebrew snapshot until React attaches
// (that is what the HTML says, nothing can change it earlier) and English from
// the post-hydration render on - the same single re-render useHydrated already
// drives for the homepage's motion, not an extra one.
//
// The stored value is read ONCE (lazy initializer), as the pages it replaces
// did. Reading it during the hydration pass is harmless - only RENDERING it
// there would mismatch.

import { useCallback, useState } from "react";
import useHydrated from "../motion/useHydrated";
import { readSiteIsHebrew, writeSiteLanguage } from "../utils/siteLanguage";

/**
 * @returns {[boolean, (isHebrew: boolean) => void]} whether the page renders in
 *   Hebrew, and a setter for the page's language toggle that also remembers
 *   the choice for the rest of the site (writeSiteLanguage).
 */
export default function useSiteIsHebrew() {
  const hydrated = useHydrated();
  const [stored] = useState(readSiteIsHebrew);
  // null = no choice made on this page yet: follow the stored preference.
  const [chosen, setChosen] = useState(null);

  const setSiteIsHebrew = useCallback((isHebrew) => {
    setChosen(isHebrew);
    writeSiteLanguage(isHebrew);
  }, []);

  let isHebrew;
  if (chosen !== null) isHebrew = chosen;
  else isHebrew = hydrated ? stored : true;

  return [isHebrew, setSiteIsHebrew];
}

export { useSiteIsHebrew };
